import { Address, TokenId } from 'nftx.js';
import { Alpha, SwapState, SwapType } from './types';
import sanitizeState from './sanitizeState';

export type SwapAction =
  | { type: 'sell_eth' }
  | { type: 'sell_vtoken'; vaultAddress: Address }
  | {
      type: 'sell_nfts';
      assetAddress: Address;
      items: SwapState['sellItems'];
      vaultId: string;
      vaultAddress: Address;
    }
  | { type: 'change_sell_amount'; amount: `${number}` }
  | { type: 'buy_eth' }
  | { type: 'buy_vtoken'; vaultAddress: Address; vaultId: string }
  | {
      type: 'buy_nfts';
      assetAddress: Address;
      vaultId: string;
      vaultAddress: Address;
      items: SwapState['buyItems'];
    }
  | { type: 'change_buy_amount'; amount: `${number}` }
  | { type: 'flip' }
  | { type: 'reset' };

export const DEFAULT_SWAP_STATE: SwapState = {
  amountController: 'sell',
  filterController: 'sell',
  buyAmount: '',
  buyVaultAddress: '',
  buyItems: [],
  buyVaultId: '',
  buyToken: '' as Address,
  buyType: 'NFT',
  sellAmount: '',
  sellToken: '' as Address,
  sellItems: [],
  sellType: 'ETH',
  sellVaultId: '',
  sellVaultAddress: '',
};

const getInitialControllers = ({
  buyAmount,
  buyType,
  sellType,
  sellAmount,
}: {
  buyAmount?: string;
  buyType?: SwapType;
  sellType?: SwapType;
  sellAmount?: string;
}): { amountController: Alpha; filterController: Alpha } => {
  if (buyType === 'NFT') {
    return { amountController: 'buy', filterController: 'buy' };
  }
  if (sellType === 'NFT') {
    return { amountController: 'sell', filterController: 'sell' };
  }
  if (buyAmount) {
    return { amountController: 'buy', filterController: 'buy' };
  }
  if (sellAmount) {
    return { amountController: 'sell', filterController: 'sell' };
  }
  return { amountController: '' as any, filterController: '' as any };
};

const omitNil = <T extends Record<string, any>>(t: T): T => {
  return Object.fromEntries(
    Object.entries(t).filter(([, v]) => v != null)
  ) as T;
};

export const getInitialState = (initialState: Partial<SwapState>) => {
  return sanitizeState({
    ...DEFAULT_SWAP_STATE,
    ...omitNil(initialState),
    ...getInitialControllers(initialState),
  });
};

const actions = {
  sell_eth(state: SwapState): SwapState {
    return {
      ...state,
      filterController: 'sell',
      sellType: 'ETH',
      sellToken: '',
      sellAmount: '',
      sellItems: [],
      sellVaultId: '',
      sellVaultAddress: '',
    };
  },
  sell_vtoken(
    state: SwapState,
    action: SwapAction & { type: 'sell_vtoken' }
  ): SwapState {
    return {
      ...state,
      filterController: 'sell',
      sellType: 'vTOKEN',
      sellToken: action.vaultAddress,
      sellItems: [],
      sellVaultId: '',
      sellVaultAddress: action.vaultAddress,
    };
  },
  sell_nfts(
    state: SwapState,
    action: SwapAction & { type: 'sell_nfts' }
  ): SwapState {
    return {
      ...state,
      amountController: 'sell',
      filterController: 'sell',
      sellType: 'NFT',
      sellToken: action.assetAddress,
      sellAmount: '',
      sellVaultId: action.vaultId,
      sellVaultAddress: action.vaultAddress,
      sellItems: action.items,
    };
  },
  change_sell_amount(
    state: SwapState,
    action: SwapAction & { type: 'change_sell_amount' }
  ): SwapState {
    return {
      ...state,
      amountController: 'sell',
      sellAmount: action.amount,
      buyAmount: '-1',
    };
  },

  buy_eth(state: SwapState): SwapState {
    return {
      ...state,
      filterController: 'buy',
      buyType: 'ETH',
      buyToken: '',
      buyAmount: '',
      buyItems: [],
      buyVaultAddress: '',
      buyVaultId: '',
    };
  },
  buy_vtoken(
    state: SwapState,
    action: SwapAction & { type: 'buy_vtoken' }
  ): SwapState {
    return {
      ...state,
      filterController: 'buy',
      buyType: 'vTOKEN',
      buyToken: action.vaultAddress,
      buyVaultId: action.vaultId,
      buyItems: [],
      buyVaultAddress: action.vaultAddress,
    };
  },
  buy_nfts(
    state: SwapState,
    action: SwapAction & { type: 'buy_nfts' }
  ): SwapState {
    return {
      ...state,
      amountController: 'buy',
      filterController: 'buy',
      buyType: 'NFT',
      buyAmount: '',
      buyToken: action.assetAddress,
      buyItems: action.items,
      buyVaultId: action.vaultId,
      buyVaultAddress: action.vaultAddress,
    };
  },
  change_buy_amount(
    state: SwapState,
    action: SwapAction & { type: 'change_buy_amount' }
  ): SwapState {
    return {
      ...state,
      amountController: 'buy',
      buyAmount: action.amount,
      sellAmount: '-1',
    };
  },
  flip(state: SwapState): SwapState {
    return {
      amountController: state.amountController === 'buy' ? 'sell' : 'buy',
      filterController: state.filterController === 'buy' ? 'sell' : 'buy',
      buyType: state.sellType,
      buyAmount: state.sellAmount,
      buyVaultId: state.sellVaultId,
      buyToken: state.sellToken,
      buyVaultAddress: state.sellVaultAddress,
      buyItems: [],
      sellType: state.buyType,
      sellAmount: state.buyAmount,
      sellToken: state.buyToken,
      sellVaultId: state.buyVaultId,
      sellItems: [],
      sellVaultAddress: state.buyVaultAddress,
    };
  },
  reset(
    state: SwapState,
    action: SwapAction & { type: 'reset' },
    initialState: Partial<SwapState>
  ): SwapState {
    return getInitialState(initialState);
  },
};

const swapStateReducer =
  (initialState: Partial<SwapState>) =>
  (state: SwapState, action: SwapAction) => {
    if (!(action.type in actions)) {
      console.warn(`Unknown action type: ${action.type}`);
      return state;
    }
    const fn = actions[action.type];
    // @ts-ignore
    const newState = fn(state, action, initialState);
    const finalState = sanitizeState(newState);
    return finalState;
  };

export default swapStateReducer;
